import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Stack, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Grid,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Paper
} from '@mui/material';
import { Add, Edit } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import axios from 'axios';

// Use axios with auth headers from localStorage
const api = {
  get:    (url, cfg={}) => axios.get(`${process.env.REACT_APP_API_URL}${url}`, { ...cfg, headers: { Authorization: `Bearer ${localStorage.getItem('sms_token')}`, 'X-School-Slug': localStorage.getItem('sms_slug'), ...cfg.headers }}),
  post:   (url, data, cfg={}) => axios.post(`${process.env.REACT_APP_API_URL}${url}`, data, { ...cfg, headers: { Authorization: `Bearer ${localStorage.getItem('sms_token')}`, 'X-School-Slug': localStorage.getItem('sms_slug'), ...cfg.headers }}),
  put:    (url, data, cfg={}) => axios.put(`${process.env.REACT_APP_API_URL}${url}`, data, { ...cfg, headers: { Authorization: `Bearer ${localStorage.getItem('sms_token')}`, 'X-School-Slug': localStorage.getItem('sms_slug'), ...cfg.headers }}),
  delete: (url, cfg={}) => axios.delete(`${process.env.REACT_APP_API_URL}${url}`, { ...cfg, headers: { Authorization: `Bearer ${localStorage.getItem('sms_token')}`, 'X-School-Slug': localStorage.getItem('sms_slug'), ...cfg.headers }}),
};

const empty = { programme_id:'', code:'', name:'', credits:15, level:1, semester:1, type:'compulsory', description:'', ca_weight:40, exam_weight:60 };
const TYPES = ['compulsory','elective','prerequisite'];
const DAYS  = ['','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

export default function Courses() {
  const { enqueueSnackbar } = useSnackbar();
  const [courses, setCourses]         = useState([]);
  const [programmes, setProgrammes]   = useState([]);
  const [filterProg, setFilterProg]   = useState('');
  const [filterLevel, setFilterLevel] = useState('');
  const [dialog, setDialog]           = useState(false);
  const [form, setForm]               = useState(empty);
  const [editing, setEditing]         = useState(null);
  const [saving, setSaving]           = useState(false);

  const load = () => {
    const params = {};
    if (filterProg)  params.programme_id = filterProg;
    if (filterLevel) params.level = filterLevel;
    api.get('/tertiary/courses', { params }).then(r => setCourses(r.data.data || []));
    api.get('/tertiary/programmes').then(r => setProgrammes(r.data.data || []));
  };

  useEffect(load, [filterProg, filterLevel]);

  const setF = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const openNew  = () => { setForm(empty); setEditing(null); setDialog(true); };
  const openEdit = c  => { setForm({ ...c, programme_id: c.programme_id||'' }); setEditing(c.id); setDialog(true); };

  const save = async () => {
    if (!form.code || !form.name) return enqueueSnackbar('Code and name required', { variant:'warning' });
    setSaving(true);
    try {
      if (editing) await api.put(`/tertiary/courses/${editing}`, form);
      else         await api.post('/tertiary/courses', form);
      enqueueSnackbar(`Course ${editing ? 'updated':'created'}`, { variant:'success' });
      setDialog(false); load();
    } catch (e) { enqueueSnackbar(e.response?.data?.message || 'Save failed', { variant:'error' }); }
    finally { setSaving(false); }
  };

  const typeColor = { compulsory:'error', elective:'success', prerequisite:'warning' };
  const levels = [...new Set(courses.map(c => c.level))].sort();

  return (
    <Box sx={{ p:3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb:3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Courses</Typography>
          <Typography variant="body2" color="text.secondary">Manage course catalogue — {courses.length} courses</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={openNew} sx={{ bgcolor:'#0f2b4a' }}>Add Course</Button>
      </Stack>

      {/* Filters */}
      <Stack direction="row" spacing={2} sx={{ mb:2 }}>
        <TextField select size="small" label="Filter by Programme" value={filterProg}
          onChange={e => setFilterProg(e.target.value)} sx={{ minWidth:220 }}>
          <MenuItem value="">All Programmes</MenuItem>
          {programmes.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Filter by Level" value={filterLevel}
          onChange={e => setFilterLevel(e.target.value)} sx={{ minWidth:140 }}>
          <MenuItem value="">All Levels</MenuItem>
          {[1,2,3,4,5,6].map(l => <MenuItem key={l} value={l}>Level {l}</MenuItem>)}
        </TextField>
      </Stack>

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius:2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor:'#f8f9fc' }}>
              {['Code','Course Name','Programme','Level','Sem','Credits','Type'].map(h => (
                <TableCell key={h} sx={{ fontWeight:700, fontSize:12 }}>{h}</TableCell>
              ))}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {courses.length === 0 ? (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py:4, color:'text.secondary' }}>No courses found</TableCell></TableRow>
            ) : courses.map(c => (
              <TableRow key={c.id} hover>
                <TableCell><Typography fontFamily="monospace" fontWeight={700} fontSize={13}>{c.code}</Typography></TableCell>
                <TableCell><Typography fontSize={13} fontWeight={500}>{c.name}</Typography></TableCell>
                <TableCell><Typography fontSize={12} color="text.secondary">{c.programme_name || '—'}</Typography></TableCell>
                <TableCell><Chip label={`Level ${c.level}`} size="small" /></TableCell>
                <TableCell><Chip label={`Sem ${c.semester}`} size="small" variant="outlined" /></TableCell>
                <TableCell fontWeight={600}>{c.credits}</TableCell>
                <TableCell><Chip label={c.type} size="small" color={typeColor[c.type]||'default'} /></TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => openEdit(c)}><Edit fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor:'#0f2b4a', color:'white' }}>{editing ? 'Edit Course':'Add Course'}</DialogTitle>
        <DialogContent sx={{ pt:'20px !important' }}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField fullWidth size="small" label="Code *" value={form.code} onChange={setF('code')} placeholder="e.g. MAT101" />
            </Grid>
            <Grid item xs={8}>
              <TextField fullWidth size="small" label="Course Name *" value={form.name} onChange={setF('name')} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" select label="Programme" value={form.programme_id} onChange={setF('programme_id')}>
                <MenuItem value="">None / Standalone</MenuItem>
                {programmes.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={3}>
              <TextField fullWidth size="small" select label="Level" value={form.level} onChange={setF('level')}>
                {[1,2,3,4,5,6].map(l => <MenuItem key={l} value={l}>Level {l}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={3}>
              <TextField fullWidth size="small" select label="Semester" value={form.semester} onChange={setF('semester')}>
                <MenuItem value={1}>Semester 1</MenuItem>
                <MenuItem value={2}>Semester 2</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={3}>
              <TextField fullWidth size="small" label="Credits" type="number" value={form.credits} onChange={setF('credits')} />
            </Grid>
            <Grid item xs={3}>
              <TextField fullWidth size="small" select label="Type" value={form.type} onChange={setF('type')}>
                {TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{display:'flex',alignItems:'center',gap:1,mb:1}}>
                <input type="checkbox" id="useDefault"
                  checked={!form.ca_weight && !form.exam_weight}
                  onChange={e=>setForm(f=>({...f,ca_weight:e.target.checked?null:40,exam_weight:e.target.checked?null:60}))}/>
                <label htmlFor="useDefault" style={{fontSize:13,cursor:'pointer'}}>Use institution default weights</label>
              </Box>
            </Grid>
            <Grid item xs={6} sx={{opacity: (!form.ca_weight && !form.exam_weight) ? 0.4 : 1}}>
              <TextField fullWidth size="small" label="CA Weight (%)" type="number" value={form.ca_weight||40}
                disabled={!form.ca_weight && !form.exam_weight}
                onChange={e=>{const v=Math.min(100,Math.max(0,parseInt(e.target.value)||0));setForm(f=>({...f,ca_weight:v,exam_weight:100-v}));}}
                inputProps={{min:0,max:100}} helperText={`Exam: ${100-(parseInt(form.ca_weight)||40)}%`}/>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Exam Weight (%)" type="number" value={form.exam_weight||60}
                onChange={e=>{const v=Math.min(100,Math.max(0,parseInt(e.target.value)||0));setForm(f=>({...f,exam_weight:v,ca_weight:100-v}));}}
                inputProps={{min:0,max:100}} helperText={`CA: ${100-(parseInt(form.exam_weight)||60)}%`}/>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Description" value={form.description} onChange={setF('description')} multiline rows={2} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving} sx={{ bgcolor:'#0f2b4a' }}>
            {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
